/**
 * Inline "delta" chip rendered from the `{+0.8 pts/100}` / `{-1.8 QSQ}`
 * markdown syntax. Green for positive, red for negative. Subtle enough to
 * sit in body copy without disrupting reading flow.
 *
 * The chip is emitted by `remarkDeltaChip` as a span with class
 * `delta-chip delta-positive|negative`. ArticleSection overrides the
 * default `span` renderer in ReactMarkdown to route those spans through
 * here.
 */

import React from "react";
import { Box } from "@mui/material";

interface DeltaChipProps {
  className?: string;
  children?: React.ReactNode;
}

export default function DeltaChip({ className, children }: DeltaChipProps) {
  const isPositive = typeof className === "string" && className.includes("delta-positive");
  const isNegative = typeof className === "string" && className.includes("delta-negative");

  // Defensive fallback: if neither class is set, render plain text.
  if (!isPositive && !isNegative) {
    return <>{children}</>;
  }

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.6,
        py: 0,
        mx: 0.25,
        borderRadius: "3px",
        fontWeight: 600,
        fontSize: "0.85em",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        color: isPositive ? "#1b6e2c" : "#a62020",
        backgroundColor: isPositive ? "rgba(40, 167, 69, 0.10)" : "rgba(207, 10, 44, 0.10)",
      }}
    >
      {children}
    </Box>
  );
}
